'use client';
import cn from 'classnames';
import { MouseEvent, ReactNode } from 'react';
import './TabBar.style.scss';
import Text from '../Text/Text';

export type Tab = {
  /** A human friendly display name for this tab */
  displayName: string;

  /** Optional custom content to render instead of displayName text */
  content?: ReactNode;

  /** A unique string value for this tab item */
  value: string;
};

type TabBarProps = {
  /** className for styling */
  className?: string;
  /** Array of the tabs to show the end user */
  tabs: Tab[];

  /** The current selected tab. Can be `null` for no selected tab */
  currentTabValue: string | null;

  /** Callback to run when a tab is clicked */
  onTabSelect: (value: string) => void;
};

function TabBar(props: TabBarProps) {
  const { className = '', tabs, currentTabValue, onTabSelect } = props;

  const handleTabSelection = (e: MouseEvent<HTMLDivElement>) => {
    const tabValue = e.currentTarget.dataset?.tabValue;

    if (tabValue) {
      onTabSelect(tabValue);
    }
  };

  return (
    <div className={cn('TabBar', className)}>
      <div className='TabBar-inner'>
        {tabs.map((tab) => {
          return (
            <div
              className={cn('TabBar-tab-wrapper cursor', {
                active: tab.value === currentTabValue,
                'tab-has-content': !!tab.content
              })}
              key={tab.value}
              role='button'
              aria-roledescription='Navigation Tab'
              onClick={handleTabSelection}
              data-tab-value={tab.value}
            >
              {tab.content ?? (
                <Text
                  uppercase
                  variant='text-m'
                >
                  {tab.displayName}
                </Text>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TabBar;
